import { addHours } from "date-fns";
import { useContext, useRef, useState, type DetailedHTMLProps, type InputHTMLAttributes } from "react";
import { useTranslation } from "react-i18next";

import { TimeSpanInputContext } from "@/contexts/TimeSpanInputContext";
import { htmlDate } from "@/fp/htmlDate";

import { ActionBox } from "./ActionBox";
import { Button } from "./Button";
import { IconButton } from "./IconButton";
import { Input, InputContainer } from "./Input";

export function EndTimeInput({
  defaultValue,
  className,
  ...props
}: { startTime?: Date } & DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>) {
  const [startTime] = useContext(TimeSpanInputContext);
  const { t } = useTranslation();
  const input = useRef<HTMLInputElement>(null);
  const [isShowingDateField, setIsShowingDateField] = useState(!!defaultValue);

  function clear() {
    if (input.current) {
      Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, "value")!.set!.call(input.current, "");
      input.current.dispatchEvent(new Event("change", { bubbles: true }));
    }
    setIsShowingDateField(false);
  }

  function setDate(date: Date) {
    if (input.current) {
      Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, "value")!.set!.call(input.current, htmlDate(date));
      input.current.dispatchEvent(new Event("change", { bubbles: true }));
      setIsShowingDateField(true);
    }
  }

  return (
    <InputContainer className={className}>
      <Input
        ref={input}
        type="datetime-local"
        className={isShowingDateField ? undefined : "hidden"}
        defaultValue={defaultValue}
        {...props}
      />
      {isShowingDateField && <IconButton type="button" name="trash" onClick={clear} />}
      {!isShowingDateField && (
        <ActionBox className="grow">
          <Button type="button" onClick={() => setDate(addHours(startTime!, 1))} disabled={!startTime}>
            {t("n-hours", { hours: 1 })}
          </Button>
          <Button type="button" onClick={() => setDate(addHours(startTime!, 3))} disabled={!startTime}>
            {t("n-hours", { hours: 3 })}
          </Button>
          <Button type="button" onClick={() => setDate(addHours(startTime!, 6))} disabled={!startTime}>
            {t("n-hours", { hours: 6 })}
          </Button>
          <Button className="ml-auto" type="button" onClick={() => setIsShowingDateField(true)}>
            {t("set-manually")}
          </Button>
        </ActionBox>
      )}
    </InputContainer>
  );
}
